<template>
  <div class="container" :class="{ reflection: isReflection }" :style="{
    color: color,
    backgroundColor: isReflection ? '#F7FBFB' : bgColor,
    fontSize: fontSize + 'px',
    textAlign: textAlign,
    width: width + '%'
  }">
    <div v-if="post.milestone">
      <Milestone v-if="post.milestone" :milestone="post.milestone" :date="post.createdAt"
        :image="post.upload ? post.upload : null"
        :descriptionOverride="showTranslation && milestoneDescriptionTranslation ? milestoneDescriptionTranslation : null" />
    </div>
    <div class="postcontainer">
      <div v-if="!post.milestone" class="title">
        <h1 v-if="!showTranslation || !titleTranslation" :style="draft ? 'opacity: 30%' : ''"
          v-html="linkify(post.title)" />
        <h1 v-if="showTranslation && titleTranslation" :style="draft ? 'opacity: 30%' : ''"
          v-html="linkify(titleTranslation)" />
        <h1 :style="draft ? 'opacity: 30%' : ''" class="text-caption" v-if="draft">{{ $t("general.post.draft-title") }}
        </h1>

        <Button v-if="
          ($store.getters.user.role === 'p' ||
            $store.getters.user.role === 'n' ||
            $store.getters.user.role === 'a') &&
          post.user.id === $store.getters.user.id
        " @click.native="openPostMenu()" margin="0" padding="0" bgcolor="transparent" width="42px"
          icon="menu-grey"></Button>
      </div>
      <p :style="draft ? 'opacity: 30%' : ''" class="information">
        <span ontouchstart>
          <span>{{ post.createdAt | moment("D MMMM YYYY - HH:mm") }} -
            {{ post.user.nickName }}</span>
          <span class="access-icon" v-if="
            isDisplayUnrestricted &&
            ($store.getters.user.role === 'p' ||
              $store.getters.user.role === 'n')
          ">
            -
            <img :src="require('@/assets/icons/globe-grey.svg')" alt="Public visibility" />
            <span class="tooltip">{{ accessTooltipText }}</span>
          </span>
          <span class="access-icon" v-if="
            isDisplayRestricted &&
            ($store.getters.user.role === 'p' ||
              $store.getters.user.role === 'n' ||
              $store.getters.user.role === 'a')
          ">
            -
            <img :src="require('@/assets/icons/lock-grey.svg')" alt="Restricted visibility" />
            <span class="tooltip">{{ accessTooltipText }}</span>
          </span>
        </span>

        <Button v-if="
          post.milestone &&
          ($store.getters.user.role === 'p' ||
            $store.getters.user.role === 'n' ||
            $store.getters.user.role === 'a') &&
          post.user.id === $store.getters.user.id
        " @click.native="openPostMenu()" margin="0" padding="0" bgcolor="transparent" width="42px"
          icon="menu-grey"></Button>
      </p>
      <!-- Reflection preview: plain truncated text, no read-more toggle (the "show all" button handles that) -->
      <p v-if="!showTranslation && isReflection" :style="draft ? 'opacity: 30%' : ''" class="reflection-preview">{{
        post.reflection && post.reflection.content ? (post.reflection.content.length > 80 ?
          post.reflection.content.substring(0, 80) + '...' : post.reflection.content) : (post.content || '') }}</p>
      <ReadMore v-if="!showTranslation && !isReflection" :style="draft ? 'opacity: 30%' : ''"
        :content="post.content ? linkify(post.content) : ''" :show-text="$t('general.feed.read-more')"
        :hide-text="$t('general.feed.read-less')" ellips="true" :max-char="250"></ReadMore>
      <ReadMore v-if="showTranslation" :style="draft ? 'opacity: 30%' : ''"
        :content="translation ? linkify(translation) : 'loading...'" :show-text="$t('general.feed.read-more')"
        :hide-text="$t('general.feed.read-less')" ellips="true" :max-char="250"></ReadMore>
      <div v-if="isReflection" :style="draft ? 'opacity: 30%' : ''" class="reflection-actions">
        <Button @click.native="$refs.reflectionComponent.openReflectionModal" :title="$t('general.reflection.show-all')"
          color="#00A9B3" bgcolor="#D9F0EA" margin="8px 0 0 0"></Button>
      </div>
      <div :style="draft ? 'opacity: 30%' : ''" class="checklist" v-if="post.checklist">
        <!-- First sort out single answer questions (weight and length) -->
        <p v-for="(c, checklistIndex) in checkListForEvolution" :key="'q1' + checklistIndex">
          <span>{{ c.question }}:&nbsp;{{ c.answer[0].answer }}</span>
        </p>

        <a v-if="
          post.checklist.find(
            c => c.question.toLowerCase() === 'gewicht' || c.question.toLowerCase() === 'lengte'
          ) &&
          (this.$store.getters.user.role === 'p' ||
            this.$store.getters.user.role === 'n' ||
            this.$store.getters.user.role === 'a')
        " :href="'/profile/evolution'" class="evolution-link">{{ $t("general.feed.check-evolution") }}!</a>

        <!-- Then show the remaining multiple choice answers -->
        <p v-for="(c, checklistIndex) in checkListWithoutEvolution" :key="'q2' + checklistIndex">
          {{ c.question }}:&nbsp;
          <span v-for="(answer, answerIndex) in c.answer" :key="answerIndex">
            <a v-if="!isNurseOrAdmin && answer.tag"
              :href="buildDictionaryTagHref(getChecklistTagSearch(answer), answer.tag.id)" class="checklist-link">#{{
                answer.answer }}</a>
            <a v-else class="checklist-link" @click.prevent="openChecklistTagDictionaryModal(answer)">
              #{{ answer.answer }}
            </a>
            {{ answerIndex === c.answer.length - 1 ? null : ", " }}
          </span>
        </p>
      </div>

      <div :style="draft ? 'opacity: 30%' : ''" class="image" v-if="post.uploadId && !post.milestone">
        <video v-if="isVideoUpload(post.upload && post.upload.mimeType)" class="post-upload-video" controls
          preload="metadata" playsinline>
          <source :src="post.upload.blobUri" :type="post.upload && post.upload.mimeType" />
          <track kind="captions" srclang="en" label="Captions unavailable" src="data:text/vtt;charset=utf-8,WEBVTT" />
        </video>
        <auto-rotate v-else>
          <img :src="post.upload.blobUri" :alt="'Foto van post ' + post.title + '.'" />
        </auto-rotate>
      </div>

      <div :style="draft ? 'opacity: 30%' : ''" class="actions">
        <div class="stats">
          <IconLink @click.native="showLikes()" :title="post.likes
            ? post.likes.length !== 1
              ? `${post.likes.length} ${$t(
                'general.feed.like.multiple-likes'
              )}`
              : `${post.likes.length} ${$t('general.feed.like.one-like')}`
            : `0 ${$t('general.feed.like.multiple-likes')}`
            " icon="heart-pink"></IconLink>
          <a v-if="$store.getters.user.role === 'p' && post.needsReadingConfirmation && !this.forceRead"
            @click="confirmRead(post.id)">{{ $t('general.feed.mark-as-read') }}</a>
          <div v-if="$store.getters.user.role === 'n' || $store.getters.user.role === 'a'">
            {{ getPostReadStatus(post) }}
          </div>
          <IconLink @click.native="toggleReactions()" :title="post.comments
            ? post.comments.length !== 1
              ? `${post.comments.length} ${$t(
                'general.feed.comment.multiple-comments'
              )}`
              : `${post.comments.length} ${$t(
                'general.feed.comment.one-comment'
              )}`
            : `0 ${$t('general.feed.comment.multiple-comments')}`
            " icon="comment-lighter"></IconLink>
        </div>

        <div class="stats tags" v-if="addedDictionaryEntries && addedDictionaryEntries.length > 0">
          <div v-if="addedDictionaryEntries.some(e => e.tags && e.tags.length > 0)">
            <img :src="require('@/assets/icons/book-lighter.svg')" height="24" width="24" class="tags-icon"
              alt="Dictionary tags" />
            <div class="tags-container">
              <span ref="tagsList" class="tags" :class="{ collapsed: !showAllTags }">
                <span class="tag-item" v-for="t in uniqueDictionaryTags" :key="`tag-${t.id}-${getTagDisplayName(t)}`">
                  <a v-if="!isNurseOrAdmin" :href="buildDictionaryTagHref(getTagDisplayName(t), t.id)">#{{
                    getTagDisplayName(t) }}</a>
                  <a v-else @click.prevent="openTagDictionaryModal(t)">
                    #{{ getTagDisplayName(t) }}
                  </a>
                </span>
              </span>
              <button v-if="hasCollapsedTagOverflow && !showAllTags" class="tags-toggle" type="button"
                @click="showAllTags = true">more...</button>
              <button v-if="hasCollapsedTagOverflow && showAllTags" class="tags-toggle" type="button"
                @click="showAllTags = false">less</button>
            </div>
          </div>
          <div v-else>
            <img :src="require('@/assets/icons/book-lighter.svg')" height="24" width="24" class="tags-icon"
              alt="Dictionary entries" />
            <span v-for="entry in addedDictionaryEntries" :key="entry.id">
              <a v-if="!isNurseOrAdmin" :href="`/profile/dictionary?entryId=${entry.id}`">{{ entry.title }}</a>&nbsp;
              <a v-if="isNurseOrAdmin">{{ entry.title }}</a>&nbsp;
            </span>
          </div>
        </div>

        <transition name="comment-animation">
          <div v-if="reactionsVisible" class="comments" :key="post.id + 'comments'">
            <div v-if="post.comments && post.comments.length > 0">
              <ul>
                <li v-for="comment in post.comments" v-bind:key="comment.id">
                  <div class="comment-header">
                    <p>
                      <strong v-if="comment.user.nickName && comment.user.nickName.length > 0">{{ comment.user.nickName
                      }}</strong>
                      <strong v-else>{{ comment.user.firstName }} {{ comment.user.lastName }}</strong>
                      <span>
                        {{ $t("general.feed.comment.on") }}
                        {{ comment.createdAt | moment("DD/MM/YYYY - HH:mm") }}
                      </span>
                    </p>

                    <Button v-if="$store.getters.user.id === comment.user.id" @click.native="
                      $modal.show('comment-menu-modal' + comment.id)
                      " margin="0" padding="0" bgcolor="transparent" width="42px" icon="menu-grey"></Button>
                    <modal v-if="$store.getters.user.id === comment.user.id" class="menu"
                      :name="'comment-menu-modal' + comment.id" width="100%" height="auto">
                      <ListItem border="1px solid #31b9be" @click.native="editComment(comment)">{{
                        $t("general.feed.comment.edit") }}</ListItem>
                      <ListItem border="1px solid #31b9be" last="true" @click.native="deleteComment(comment.id)">{{
                        $t("general.feed.comment.delete") }}</ListItem>
                    </modal>
                  </div>
                  <p v-if="!showTranslation || !commentTranslations" v-html="comment.content" />
                  <p v-if="showTranslation && commentTranslations" v-html="commentTranslations[comment.id]" />
                </li>
              </ul>
            </div>
            <div v-else class="comments-empty" :key="post.id + 'nocomments'">
              <p>{{ $t("general.feed.comment.not-found") }}</p>
            </div>
          </div>
        </transition>
        <div class="comment">
          <Button @click.native="likePost(post.id)" title shadow="true" color="#00A9B3"
            :bgcolor="likesHasUser() ? '#F3BDBF' : '#D9F0EA'" width="44px" margin="0" padding="0"
            :icon="likesHasUser() ? 'heart-filled' : 'heart-blue'" :shouldAnimate="!likesHasUser()"></Button>
          <Button v-ripple="'rgba(255, 255, 255, 0.35)'" shadow="true" :title="$t('general.feed.comment.comment')"
            color="#00A9B3" bgcolor="#D9F0EA" width="100%" :margin="$store.getters.user.role === 'p' ? '0 10px 0 10px' : '0 0 0 10px'
              " @click.native="$modal.show('comment-modal' + post.id)"></Button>
          <Button @click.native="toggleTranslation(post.id)" title v-if="userHasTranslation" shadow="true"
            color="#00A9B3" :bgcolor="showTranslation ? '#F3BDBF' : '#D9F0EA'" width="44px" margin="0" padding="1"
            icon="translate"></Button>
        </div>
      </div>
    </div>

    <ShareModal :post="{
      postId: post.id,
      title: post.title,
      content: post.content,
      nick: post.user.nickName,
      createdAt: post.createdAt,
      milestone: post.milestone,
      uploadId: post.uploadId
    }" />
    <Reflection ref="reflectionComponent" :post="post" />
    <LikedModal :postId="post.id" :likes="post.likes" />
    <EditPostModal :post="{
      postId: post.id,
      title: post.title,
      content: post.content,
      isDraft: post.isDraft,
      access: post.access,
      uploadId: post.uploadId,
      addedDictionaryEntries: post.addedDictionaryEntries,
      milestone: post.milestone,
      reflectionId: reflectionId
    }" @delete-post="deletePost" />
    <CommentModal :postId="post.id" :title="post.title" @post-comment="postComment" />
    <TagDictionaryEntriesModal ref="tagDictionaryEntriesModal" :postId="post.id" />

    <modals-container @comment-edit="$emit('comment-edit', $event)" />
  </div>
</template>

<script>
import { neoparentPosts, neoparentReflections } from "@/api";
import Button from "@/page_elements/Button";
import TextAreaField from "@/page_elements/TextAreaField";
import Reflection from "@/page_elements/Reflection";
import TagDictionaryEntriesModal from "@/page_elements/Modals/TagDictionaryEntriesModal";
import linkifyStr from "linkifyjs/string";

export default {
  name: "Post",
  components: {
    Button,
    TextAreaField,
    Reflection,
    TagDictionaryEntriesModal
  },
  props: {
    draft: { default: false },
    post: { required: true },
    addedDictionaryEntries: { required: true, default: () => [] },
    checklist: { default: null },

    color: { default: "#000" },
    fontSize: { default: 14 },
    bgColor: { default: "#fff" },
    width: { default: 100 },
    textAlign: { default: "left" }
  },
  computed: {
    reflectionId() {
      return this.post.reflectionId || this.post.ReflectionId || null;
    },
    isReflection() {
      return !!this.reflectionId;
    },
    accessTokensForDisplay() {
      if (this.isReflection && (this.post.reflection || this.post.Reflection)) {
        const reflection = this.post.reflection || this.post.Reflection;
        const tokens = new Set(["self"]);

        if (reflection.isVisibleToParents) {
          tokens.add("parents");
        }

        if (reflection.isVisibleToHospital) {
          tokens.add("hospital");
        }

        if (reflection.isVisibleToThirdParties) {
          tokens.add("third");
        }

        return tokens;
      }

      return this.parsePostAccess(this.post.access);
    },
    isDisplayUnrestricted() {
      if (this.isReflection && (this.post.reflection || this.post.Reflection)) {
        const reflection = this.post.reflection || this.post.Reflection;
        // Globe when any checkbox besides "only myself" is checked
        return !!(reflection.isVisibleToParents || reflection.isVisibleToHospital || reflection.isVisibleToThirdParties);
      }
      return this.accessTokensForDisplay.has("third");
    },
    isDisplayRestricted() {
      return !this.isDisplayUnrestricted;
    },
    accessTooltipText() {
      if (this.isReflection && (this.post.reflection || this.post.Reflection)) {
        const reflection = this.post.reflection || this.post.Reflection;
        const labels = [this.$t("general.reflection.visibility-self")];

        if (reflection.isVisibleToParents) {
          labels.push(this.$t("general.reflection.visibility-parents"));
        }

        if (reflection.isVisibleToHospital) {
          labels.push(this.$t("general.reflection.visibility-hospital"));
        }

        if (reflection.isVisibleToThirdParties) {
          labels.push(this.$t("general.reflection.visibility-third-parties"));
        }

        return labels.join(", ");
      }

      return this.isDisplayUnrestricted
        ? this.$t("general.feed.unrestricted")
        : this.$t("general.feed.restricted");
    },
    userHasTranslation() {
      const user = this.$store.getters.user;
      const hasDeepl = !!(user && user.hospital && user.hospital.hasDeepl);

      if (!hasDeepl || !user) {
        return false;
      }

      if (user.role !== 'p') {
        return true;
      }

      return !!user.translationLanguage;
    },
    checkListForEvolution: function () {
      return this.post.checklist.filter(c => c.question.toLowerCase() === 'gewicht' || c.question.toLowerCase() === 'lengte');
    },
    checkListWithoutEvolution: function () {
      return this.post.checklist.filter(c => c.question.toLowerCase() !== 'gewicht' && c.question.toLowerCase() !== 'lengte');
    },
    uniqueDictionaryTags() {
      const deduplicated = [];
      const seen = new Set();

      this.addedDictionaryEntries.forEach(entry => {
        if (!entry.tags || entry.tags.length === 0) {
          return;
        }

        entry.tags.forEach(tag => {
          const label = this.getTagDisplayName(tag);
          const key = label ? label.trim().toLowerCase() : "";

          if (!key || seen.has(key)) {
            return;
          }

          seen.add(key);
          deduplicated.push(tag);
        });
      });

      return deduplicated;
    },
    hasCollapsedTagOverflow() {
      return this.tagOverflowDetected;
    },
    isNurseOrAdmin() {
      return this.$store.getters.user.role === "n" || this.$store.getters.user.role === "a";
    },
  },
  data: function () {
    return {
      reactionsVisible: false,
      renderedTags: [],
      forceRead: false,
      showAllTags: false,
      tagOverflowDetected: false,
      showTranslation: false,
      translation: null,
      titleTranslation: null,
      milestoneDescriptionTranslation: null,
      commentTranslations: []
    };
  },
  mounted() {
    window.addEventListener("resize", this.updateTagOverflow);
    this.updateTagOverflow();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateTagOverflow);
  },
  updated() {
    if (!this.showAllTags) {
      this.updateTagOverflow();
    }
  },
  methods: {
    getPostModalId() {
      return this.post.id || this.post.postId;
    },
    openPostMenu() {
      const postId = this.getPostModalId();
      if (!postId) {
        return;
      }

      this.$modal.show("menu-modal" + postId);
    },
    parsePostAccess(access) {
      if (!access || !access.trim()) {
        return new Set(["parents"]);
      }

      const normalized = access.trim().toLowerCase();

      if (normalized === "pub") {
        return new Set(["parents", "hospital", "third"]);
      }

      if (normalized === "priv") {
        return new Set(["parents", "hospital"]);
      }

      return new Set(
        access
          .split(/[,;\s]+/)
          .map(token => token.trim().toLowerCase())
          .filter(Boolean)
      );
    },
    isPostUnrestricted(access) {
      const tokens = this.parsePostAccess(access);
      return tokens.has("third");
    },
    isPostRestricted(access) {
      return !this.isPostUnrestricted(access);
    },
    getPostReadStatus(post) {
      if (post.needsReadingConfirmation) {
        return this.$t('general.feed.read-not-confirmed')
      }
      return '';
    },
    async toggleTranslation(postId) {
      this.showTranslation = !this.showTranslation;
      if (!this.translation && this.showTranslation) {
        const res = await neoparentPosts.translatePost(postId)
        this.translation = res.data.translation;
        this.commentTranslations = res.data.commentTranslations;
        this.titleTranslation = res.data.titleTranslation;
        this.milestoneDescriptionTranslation = res.data.milestoneDescriptionTranslation || null;
        if (this.$store.getters.user.role === 'p') {
          this.forceRead = true;
        }
      }
    },
    deletePost(id, confirmedWithImageModal = false) {
      const postId = id || this.getPostModalId();
      if (!postId) return;

      const shouldAskBrowserConfirm = !this.post.uploadId && !confirmedWithImageModal;
      const shouldDelete = !shouldAskBrowserConfirm || confirm(this.$t("general.post.confirm"));
      if (!shouldDelete) {
        return;
      }

      if (this.reflectionId) {
        neoparentReflections.delete(this.reflectionId)
          .then(() => this.$emit("post-delete-success"))
          .catch(err => {
            this.error = err.response.data.message;
            this.$emit("post-delete-failed");
          });
      } else {
        neoparentPosts.deletePost(postId, { deleteImage: confirmedWithImageModal })
          .then(() => this.$emit("post-delete-success"))
          .catch(err => {
            this.error = err.response.data.message;
            this.$emit("post-delete-failed");
          });
      }
    },
    confirmRead(id) {
      neoparentPosts
        .postRead(id)
        .then(result => {
          this.$emit("post-like-success");
        })
        .catch(err => {
          console.log(err);
          this.error = err.response.data.message;
          this.$emit("post-like-failed");
        });
    },
    likePost(id) {
      neoparentPosts
        .likePost(id)
        .then(result => {
          this.$emit("post-like-success");
        })
        .catch(err => {
          console.log(err);
          this.error = err.response.data.message;
          this.$emit("post-like-failed");
        });
    },
    showLikes: function () {
      this.$modal.show("likes-modal" + this.post.id);
    },
    likesHasUser: function () {
      const user = this.$store.getters.user;
      if (this.post.likes && this.post.likes.length > 0) {
        if (
          this.post.likes.find(like => {
            return like.user.id === user.id;
          })
        ) {
          return true;
        }
        return false;
      }
    },
    toggleReactions() {
      this.reactionsVisible = !this.reactionsVisible;
    },
    getTagDisplayName(tag) {
      if (!tag) {
        return "";
      }

      return this.showTranslation && tag.shadowName ? tag.shadowName : tag.name;
    },
    getChecklistTagSearch(answer) {
      if (!answer) {
        return "";
      }

      const displayText = (answer.answer || "").trim();
      if (displayText) {
        return displayText;
      }

      return answer.tag && answer.tag.name ? answer.tag.name : "";
    },
    buildDictionaryTagHref(search, tagId) {
      const params = [];
      if (search) {
        params.push(`search=${encodeURIComponent(search)}`);
      }
      if (tagId) {
        params.push(`tagId=${encodeURIComponent(tagId)}`);
      }

      return params.length > 0
        ? `/profile/dictionary?${params.join("&")}`
        : "/profile/dictionary";
    },
    openTagDictionaryModal(tag) {
      if (!this.isNurseOrAdmin || !tag) {
        return;
      }

      if (!this.$refs.tagDictionaryEntriesModal) {
        return;
      }

      const displayName = (tag.displayName || this.getTagDisplayName(tag) || "").trim();
      const defaultName = (tag.name || "").trim();
      const preferTranslated =
        !!displayName &&
        !!defaultName &&
        displayName.toLowerCase() !== defaultName.toLowerCase();

      this.$refs.tagDictionaryEntriesModal.openForTag({
        id: tag.id || null,
        name: tag.name || "",
        displayName,
        preferTranslated
      });
    },
    openChecklistTagDictionaryModal(answer) {
      if (!answer) {
        return;
      }

      this.openTagDictionaryModal({
        id: answer.tag && answer.tag.id ? answer.tag.id : null,
        name: answer.tag && answer.tag.name ? answer.tag.name : "",
        displayName: this.getChecklistTagSearch(answer)
      });
    },
    updateTagOverflow() {
      this.$nextTick(() => {
        const tagsElement = this.$refs.tagsList;
        if (!tagsElement || this.showAllTags) {
          return;
        }

        this.tagOverflowDetected = tagsElement.scrollHeight > tagsElement.clientHeight + 1;
      });
    },
    postComment({ postId, comment }) {
      this.reactionsVisible = true;
      this.$emit("comment", {
        postId,
        comment
      });
    },
    deleteComment(id) {
      if (confirm(this.$t("general.feed.comment.confirm"))) {
        neoparentPosts
          .deleteCommentFromPost(id)
          .then(result => {
            this.$emit("comment-delete-success");
          })
          .catch(err => {
            console.log(err);
            this.error = err.response.data.message;
            this.$emit("comment-delete-failed");
          });
      }
    },
    editComment(comment) {
      this.$modal.hide("comment-menu-modal" + comment.id);
      this.$modal.show(
        {
          template: `
            <div>
              <Button
                class="close"
                @click.native="$modal.hide($parent.name)"
                margin="10px"
                color="#31b9be"
                bgcolor="transparent"
                width="30px"
                padding="0"
                icon="close-blue"
              ></Button>
              <h3>{{ title }}</h3>
              <TextAreaField rows="3" v-model="content" labelTitle placeholder="Zeg iets liefs"></TextAreaField>
              <Button
                title="${this.$t("general.feed.comment.edit")}"
                shadow="true"
                @click.native="$emit('comment-edit', {
                    postId,
                    commentId: comment.id,
                    content
                  }); $modal.hide($parent.name);"
                margin="1rem 0"
              ></Button>
            </div>
          `,
          components: {
            TextAreaField,
            Button
          },
          data: function () {
            return {
              content: this.comment.content
            };
          },
          props: {
            title: {},
            comment: {},
            postId: {},
            editComment: {}
          }
        },
        {
          title: this.post.title,
          comment: comment,
          postId: this.post.id
        },
        {
          height: "auto",
          width: "90%",
          class: "comment"
        }
      );
    },
    linkify: function (text) {
      //return linkifyStr(text);
      return text
    },
    isVideoUpload(mimeType) {
      return (mimeType || "").toLowerCase().startsWith("video/");
    }
  }
};
</script>

<style scoped lang="scss">
.container {
  transition: background-color 0.5s ease;
  font-size: 14px;
}

.postcontainer {
  padding: 0 5%;
}

h1 {
  font-size: 24px;
  margin: 0;
}

.title {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;

  h1 {
    flex: 1;
    min-width: 0;
    line-height: 1.3;
  }
}

.information {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  letter-spacing: 0.15px;
  color: #c6c6c6;
  margin: 8px 0 16px 0;

  >span:first-child {
    flex: 1;
    min-width: 0;
    line-height: 1.45;
  }

  .access-icon {
    position: relative;

    img {
      max-height: 24px;
      max-width: 24px;
      vertical-align: middle;
    }

    .tooltip {
      display: none;
      position: absolute;
      z-index: 100;
      background: #fff;
      border: 1px solid #00a9b3;
      border-radius: 10px;
      width: 200px;
      padding: 4px 8px;
      left: 32px;
      color: #333;
      top: 0;
    }

    &:hover .tooltip,
    &:focus .tooltip {
      display: block;
    }
  }
}

.content {
  margin: 16px 0;
}

.content p {
  display: inline;
}

p {
  margin: 0;
}

a {
  cursor: pointer;
}

.container.reflection {
  background: #F7FBFB;
  border-left: 4px solid #00A9B3;
}

.reflection-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #00A9B3;
  margin-bottom: 4px;
}

// // https://vue-loader.vuejs.org/guide/scoped-css.html#child-component-root-elements
// This works but is currently defined in root of the ap p (App.vue)
// >>> .linkified {
//   color: $main;
// }

.actions .stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 12px;
  color: #c6c6c6;
  text-decoration: none;
  margin: 16px 0;
}

.actions .stats.tags {
  margin: 0 0 16px;
}

.tags-icon {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}

.tags-container {
  padding-left: 0;
  min-width: 0;

  .tags {
    display: flex;
    flex-wrap: wrap;
    min-width: 0;
  }

  .tags.collapsed {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .tag-item {
    display: inline-block;
    margin-right: 6px;
  }

  .tags-toggle {
    border: 0;
    background: transparent;
    padding: 0;
    margin-left: 6px;
    color: #8a949c;
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
  }

  .tags-toggle:hover,
  .tags-toggle:focus {
    color: #6f7a84;
    text-decoration: none;
  }

  a {
    margin: 0 3px;
  }
}

.action .stats .extraspace {
  display: block;
  height: 8px;
}

.actions .stats a {
  color: #c6c6c6;
  text-decoration: underline;
}

.actions .comment {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
}

.comment-header p {
  line-height: 30px;
  flex: 1;
  min-width: 0;
}

.comments li {
  min-width: 100%;
  padding: 8px 16px;
  border: 1px solid #afdfd3;
  border-radius: 16px;
  margin-bottom: 16px;
}

.comments li span {
  font-size: 12px;
  color: #666;
}

.reflection-preview {
  line-height: 1.5;
  word-break: break-word;
}

.reflection-actions {
  margin-bottom: 8px;
}

.image img {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 10px;
}

.post-upload-video {
  width: 100%;
  max-height: 540px;
  display: block;
  border-radius: 10px;
  background: #000;
}

.comments-empty {
  margin-bottom: 16px;
}

.comment-animation-enter-active {
  transition: all 0.3s ease-out;
}

.comment-animation-leave-active {
  transition: 0;
}

.comment-animation-enter,
.comment-animation-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

.checklist-link {
  text-decoration: underline;
  color: #c6c6c6;
  cursor: pointer;
}

.evolution-link {
  color: #00a9b3;
  margin-bottom: 0.5rem;
  display: inline-block;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

::v-deep li {
  display: list-item !important;
  list-style: disc !important;

}

a {
  cursor: pointer;
  text-decoration: underline;
}

@media (max-width: 768px) {
  .container {
    font-size: 16px;
  }

  .postcontainer {
    padding: 0 2px;
  }

  h1 {
    font-size: 20px;
  }

  .information {
    font-size: 13px;
    margin: 8px 0 14px;
  }

  .information .access-icon .tooltip {
    width: 170px;
    left: auto;
    right: 0;
    top: 24px;
  }

  .actions .stats {
    margin: 14px 0;
  }

  .actions .stats.tags>div {
    width: 100%;
    display: flex;
    align-items: flex-start;
    gap: 8px;
  }

  .tags-container {
    width: 100%;
  }

  .tags-container .tags.collapsed {
    -webkit-line-clamp: 3;
    line-clamp: 3;
  }

  .comments li {
    padding: 10px 12px;
    border-radius: 12px;
  }

  .comment {
    padding-bottom: 2px;
  }

  .actions .comment {
    align-items: stretch;
  }

  .actions .comment>button:nth-child(2) {
    height: auto !important;
    min-height: 54px;
    padding: 8px 14px !important;
    line-height: 1.25;
    margin: 0 6px !important;
  }
}

@media (hover: none) and (pointer: coarse) {
  .container {
    font-size: 17px;
    line-height: 1.5;
  }

  .postcontainer {
    padding: 0 2px;
  }

  h1 {
    font-size: 22px;
    line-height: 1.35;
  }

  .information {
    font-size: 14px;
  }

  .comments li span {
    font-size: 13px;
  }

  .actions .stats {
    margin: 14px 0;
    gap: 10px 14px;
  }

  .actions .comment {
    gap: 8px;
  }

  .actions .comment>button:nth-child(2) {
    height: auto !important;
    min-height: 58px;
    padding: 10px 14px !important;
    line-height: 1.25;
    margin: 0 4px !important;
  }
}
</style>
